// ManageBookingPage.js - Premium Design
import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { formatCurrency, formatPhoneDisplay } from '../utils/helpers';
import APIService from '../services/api';
import { useBooking } from '../hooks/useBooking';
import { PAGES } from '../utils/constants';
import logoMark from '../assets/images/logo.png';

const ManageBookingPage = () => {
  const { state: { manageBookingId }, setCurrentPage } = useBooking(); 
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [bookingId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('manage') || manageBookingId;
  });

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setError('No booking ID provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const bookingData = await APIService.request(`/bookings/magic/${bookingId}`);
        setBooking(bookingData || null);
      } catch (err) {
        setError('Booking not found or invalid link.');
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  const handleCancelBooking = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      setCancelLoading(true);
      await APIService.request(`/bookings/code/${manageBookingId || booking.code}`, { method: 'DELETE' });
      setBooking(prev => ({ ...prev, status: 'cancelled' }));
      alert('Booking cancelled successfully.');
    } catch (err) {
      alert('Failed to cancel booking. Contact support.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const formatDate = (isoString) => {
    if (!isoString) return 'Not specified';
    return new Date(isoString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'Not specified';
    return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getExtraTotal = (extra) => {
    const totalVal = parseFloat(extra.total || 0);
    if (totalVal > 0) return totalVal;
    
    const uppercaseCode = extra.code?.toUpperCase();
    let fallbackUnitPrice = 0;
    if (uppercaseCode === 'CHILD_SEAT') {
      fallbackUnitPrice = booking?.currency === 'LKR' ? 1650.00 : (booking?.currency === 'USD' ? 5.50 : 5.00);
    } else if (uppercaseCode === 'BOOSTER_SEAT') {
      fallbackUnitPrice = booking?.currency === 'LKR' ? 990.00 : (booking?.currency === 'USD' ? 3.30 : 3.00);
    }
    
    return fallbackUnitPrice * Number(extra.quantity || 0);
  };

  const calculateExtrasTotal = () => {
    if (!booking?.extras) return 0;
    return booking.extras.reduce((total, extra) => total + getExtraTotal(extra), 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-8 rounded-3xl text-center">
            <div className="w-12 h-12 border-4 border-white/10 border-t-blue-500 rounded-full animate-spin mb-6 mx-auto"></div>
            <p className="text-white font-bold tracking-widest uppercase text-xs">Loading Secure Data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !booking) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-10 rounded-[2.5rem] text-center max-w-md border-red-500/20 shadow-2xl">
            <div className="text-red-500 text-5xl mb-6">⚠️</div>
            <h1 className="text-2xl font-black text-white mb-2 uppercase tracking-tighter">System Error</h1>
            <p className="text-gray-400 text-sm mb-8 leading-relaxed font-medium">{error || 'The booking link is invalid or has expired.'}</p>
            <a href="/" className="inline-block bg-blue-600 text-white px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20 active:scale-95 transition-all">Start New Booking</a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showHeaderMobile={false}>
      {/* Mobile Page Header */}
      <div className="lg:hidden">
        <div className="container mx-auto px-4 py-6 relative flex items-center justify-center">
          <button onClick={() => window.history.back()} className="p-3 bg-white/5 border border-white/10 rounded-full absolute left-2 text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="font-black text-[10px] uppercase tracking-[0.2em] text-blue-500">Manage Booking</div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12 pb-32">
        <div className="max-w-4xl mx-auto">
          
          {/* Main Content Area */}
          <main className="glass-panel rounded-[2.5rem] overflow-hidden border-white/5 shadow-2xl print:shadow-none print:border-none print:bg-white print:text-black">
            
            {/* Branding & Ref */}
            <div className="px-8 md:px-12 py-10 flex flex-col md:flex-row justify-between items-start gap-8 border-b border-white/5 print:border-gray-200">
              <div 
                className="flex items-center gap-5 cursor-pointer group select-none"
                onClick={() => setCurrentPage(PAGES.HOME)}
              >
                <img 
                  src={logoMark} 
                  alt="Logo" 
                  className="w-16 h-16 object-contain p-2 bg-white/5 rounded-2xl border border-white/10 print:bg-transparent transition-all duration-300 group-hover:scale-105 group-hover:border-blue-500/50 group-hover:shadow-[0_0_15px_rgba(59,130,246,0.2)]" 
                />
                <div>
                  <h1 className="text-2xl md:text-3xl font-black text-white tracking-tighter uppercase print:text-black group-hover:text-blue-500 transition-colors duration-300">LankaWayFinder</h1>
                  <div className="text-[10px] text-blue-500 font-black tracking-[0.3em] uppercase mt-1 transition-all duration-300 group-hover:tracking-[0.35em] group-hover:text-blue-400">Manage Dashboard</div>
                </div>
              </div>
              <div className="md:text-right">
                <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">Booking Reference</div>
                <h2 className="text-3xl font-black text-white tracking-tighter print:text-black">{booking.code}</h2>
                <div className="inline-block mt-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.1em] bg-blue-500/10 text-blue-500 border border-blue-500/20">
                  {booking.status}
                </div>
              </div>
            </div>

            {/* Status Alert Area */}
            <div className={`px-8 md:px-12 py-6 border-b border-white/5 ${
              booking.status === 'confirmed' ? 'bg-green-500/5' :
              booking.status === 'cancelled' ? 'bg-red-500/5' :
              'bg-blue-500/5'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                  booking.status === 'confirmed' ? 'bg-green-500 text-white' :
                  booking.status === 'cancelled' ? 'bg-red-500 text-white' :
                  'bg-blue-500 text-white'
                }`}>
                  {booking.status === 'confirmed' ? '✓' : booking.status === 'cancelled' ? '✗' : '!'}
                </div>
                <div>
                  <p className="text-sm font-black text-white uppercase tracking-wider print:text-black">
                    {booking.status === 'confirmed' ? 'FULLY PAID & CONFIRMED' : 
                     booking.status === 'cancelled' ? 'BOOKING CANCELLED' : 'ACTION REQUIRED'}
                  </p>
                  <p className="text-xs text-gray-400 font-medium">
                    {booking.status === 'confirmed' ? 'Your luxury transfer is scheduled. Chauffeur details will be sent via WhatsApp.' :
                     booking.status === 'cancelled' ? 'This booking is no longer active and has been removed from our schedule.' :
                     'Pending final verification or payment processed.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 md:p-12 space-y-16">
              {/* Grid 1: Journey */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Itinerary Information</h3>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-1 bg-blue-500 rounded-full"></div>
                      <div>
                        <div className="text-[10px] text-gray-500 font-black uppercase mb-1">Pickup Point</div>
                        <div className="text-sm font-bold text-white leading-snug print:text-black">{booking.fromPlace}</div>
                        <div className="text-xs text-blue-500 font-bold mt-1 uppercase">
                          {formatDate(booking.pickupAt)} • {formatTime(booking.pickupAt)}
                        </div>
                      </div>
                    </div>
                    {booking.toPlace && (
                      <div className="flex gap-4">
                        <div className="w-1 bg-green-500 rounded-full"></div>
                        <div>
                          <div className="text-[10px] text-gray-500 font-black uppercase mb-1">Destination</div>
                          <div className="text-sm font-bold text-white leading-snug print:text-black">{booking.toPlace}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Vehicle Selection</h3>
                  <div className="flex items-center gap-5 p-4 bg-white/5 rounded-2xl border border-white/10">
                    <div className="w-12 h-8 bg-white/10 rounded flex items-center justify-center">
                      <div className="w-8 h-4 bg-blue-500/50 rounded-sm"></div>
                    </div>
                    <div>
                      <div className="text-sm font-black text-white print:text-black">{booking.priceSnapshot?.vehicleClassName || 'Premium Fleet'}</div>
                      <div className="text-[10px] text-gray-500 font-bold uppercase mt-1">
                        👥 {booking.pax} PAX • 🧳 {booking.bags} BAGS
                      </div>
                    </div>
                  </div>
                  {booking.flightNumber && (
                    <div className="mt-4 p-4 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center gap-3">
                      <span className="text-lg">✈️</span>
                      <div>
                        <div className="text-[9px] text-blue-500 font-black uppercase">Flight Monitored</div>
                        <div className="text-xs font-bold text-white">{booking.flightNumber}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Grid 2: Passenger */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Passenger Records</h3>
                  <div className="space-y-1">
                    <div className="text-lg font-black text-white print:text-black">{booking.customerName}</div>
                    <div className="text-sm text-gray-400 font-bold">{booking.customerEmail}</div>
                    <div className="text-sm text-blue-500 font-black mt-2">{formatPhoneDisplay(booking.customerPhone)}</div>
                  </div>
                  {booking.notes && (
                    <div className="mt-6 p-4 bg-white/5 border border-white/5 rounded-2xl">
                      <div className="text-[9px] text-gray-500 font-black uppercase tracking-wider mb-1">Message for Chauffeur / Special Notes</div>
                      <div className="text-xs text-gray-300 font-medium leading-relaxed italic">
                        "{booking.notes}"
                      </div>
                    </div>
                  )}
                </div>
                {booking.extras && booking.extras.length > 0 && (
                  <div>
                    <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Additional Extras</h3>
                    <div className="space-y-3">
                      {booking.extras.map((extra, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white/5 p-3 rounded-xl border border-white/5">
                          <span className="text-xs font-bold text-gray-300">{extra.name} × {extra.quantity}</span>
                          <span className="text-xs font-black text-white">{formatCurrency(getExtraTotal(extra), booking.currency)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Grid 3: Financials */}
              {(() => {
                const extrasTotal = calculateExtrasTotal();
                const rawBasePrice = parseFloat(booking.priceSnapshot?.basePrice || booking.basePrice || booking.totalAmount || 0);
                const rawTotalAmount = parseFloat(booking.totalAmount || 0);

                let displayBasePrice = rawBasePrice > 0 ? rawBasePrice : (rawTotalAmount - extrasTotal);
                let displayTotalAmount = rawTotalAmount;

                if (extrasTotal > 0 && (rawBasePrice === 0 || Math.abs(rawBasePrice - rawTotalAmount) < 0.01)) {
                  displayTotalAmount = rawTotalAmount + extrasTotal;
                  displayBasePrice = rawTotalAmount;
                } else if (extrasTotal > 0 && rawTotalAmount > extrasTotal) {
                  displayBasePrice = rawTotalAmount - extrasTotal;
                }

                return (
                  <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5">
                    <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-10 text-center">Financial Settlement</h3>
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Base Tariff</span>
                        <span className="text-white font-black">{formatCurrency(displayBasePrice, booking.currency)}</span>
                      </div>
                      {extrasTotal > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Service Extras</span>
                          <span className="text-white font-black">{formatCurrency(extrasTotal, booking.currency)}</span>
                        </div>
                      )}
                      <div className="pt-6 border-t border-white/10 flex justify-between items-center">
                        <div className="text-2xl font-black text-white tracking-tighter uppercase">Total Amount</div>
                        <div className="text-3xl font-black text-blue-500 tracking-tighter">{formatCurrency(displayTotalAmount, booking.currency)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Management Actions */}
              <div className="pt-8 border-t border-white/5 print:hidden">
                <h3 className="text-[12px] font-black text-white uppercase tracking-widest mb-8 text-center">Dashboard Control</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <button onClick={handlePrint} className="flex flex-col items-center gap-3 p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 transition-all active:scale-95 group">
                    <span className="text-2xl group-hover:scale-110 transition-transform">🖨️</span>
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">Print Invoice</span>
                  </button>
                  <a href={`mailto:support@lankawayfinder.com?subject=Booking Support - #${booking.code}`} className="flex flex-col items-center gap-3 p-6 bg-blue-600/10 rounded-3xl border border-blue-500/20 hover:bg-blue-600/20 transition-all active:scale-95 group">
                    <span className="text-2xl group-hover:scale-110 transition-transform">📧</span>
                    <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Email Support</span>
                  </a>
                  {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                    <button onClick={handleCancelBooking} disabled={cancelLoading} className="flex flex-col items-center gap-3 p-6 bg-red-600/10 rounded-3xl border border-red-500/20 hover:bg-red-600/20 transition-all active:scale-95 group disabled:opacity-30">
                      <span className="text-2xl group-hover:scale-110 transition-transform">❌</span>
                      <span className="text-[10px] font-black text-red-500 uppercase tracking-widest">{cancelLoading ? 'Processing...' : 'Cancel Booking'}</span>
                    </button>
                  )}
                </div>
              </div>

            </div>
          </main>

          {/* Footer Contact */}
          <div className="text-center mt-12 text-gray-500 text-xs font-medium uppercase tracking-[0.2em] print:hidden">
            LankaWayFinder Chauffeur Operations • Colombo, Sri Lanka
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          @page { margin: 0.5in; size: A4; }
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
          body { font-size: 11pt !important; background: white !important; color: black !important; }
          .print\\:hidden { display: none !important; }
          .glass-panel { border: none !important; background: white !important; box-shadow: none !important; }
          .text-white { color: black !important; }
          .text-gray-400, .text-gray-500 { color: #666 !important; }
          .border-white\\/5 { border-color: #eee !important; }
          .bg-white\\/5 { background: #f9f9f9 !important; border: 1px solid #eee !important; }
        }
      `}</style>
    </Layout>
  );
};

export default ManageBookingPage;